import type { Dict } from '../components/utils';
import { dictToQuery } from '../components/utils';
import type { AllPermissionActions, OTPAuthType, StorageProvider } from '@/types/types';
import { APIName, BackendServices, BackendTypes, Endpoint, } from './interfaces';

export const BASEURL: Record<BackendTypes, () => APIName> = {
    backend: () => 'specterx',
    localhost: () => 'localhost',
    backendProxy: () => 'specterxProxy',
};

const ACCESS_ENDPOINTS = {
    // files
    createFolder: (): Endpoint => [BackendServices.access, 'folders'], // POST
    getFiles: (query: string): Endpoint => [BackendServices.access, `files${query}`], // GET
    getMultipleFiles: (filesIds: string[]): Endpoint => (
        [BackendServices.access, `files/multiple?files_ids=${filesIds}`]
    ), // GET
    getFilePath: (fileId: string): Endpoint => (
        [BackendServices.access, `files/${fileId}/breadcrumbs?full_metadata=true`]
    ), // GET
    getFileDetails: (fileId): Endpoint => [BackendServices.access, `files/${fileId}?use_command_api=true`], // GET
    openFile: (fileId: string): Endpoint => [BackendServices.access, `files/${fileId}/open`], // POST
    verifyAccess: (fileId: string): string => `${BackendServices.access}files/${fileId}/verify-access`, // POST
    deleteFile: (fileId: string): Endpoint => [BackendServices.access, `files/${fileId}`], // DELETE
    moveFiles: (folderId: string): Endpoint => [BackendServices.access, `folders/${folderId}/move`], // PUT
    newFile: (): Endpoint => [BackendServices.access, 'files'], // POST
    getFileMetadata: (fileId: string): Endpoint => [BackendServices.access, `files/${fileId}/metadata`], // GET
    // permissions
    getSharedFiles: (origin: string, filesIdsQueryString: string): Endpoint => (
        [BackendServices.access, `files?origin=${origin}${filesIdsQueryString}&allowUnmanaged=true`]
    ),
    shareFiles: (): Endpoint => [BackendServices.access, 'share'], // POST
    shareFile: (fileId: string, userId = ''): Endpoint => {
        const URLLastPart = userId === '' ? `${fileId}/permissions` : `${fileId}/permissions/${userId}`;
        return [BackendServices.access, `files/${URLLastPart}`];
    }, // GET, PUT, PATCH
    checkAbilityTo: (fileId: string, userId: string, action: AllPermissionActions): Endpoint => (
        [BackendServices.access, `files/${fileId}/permissions/${userId}?action=${action}`]
    ), // GET
    revoke: (fileId: string): Endpoint => [BackendServices.access, `${fileId}/revoke`], // POST
    getEmails: (): Endpoint => [BackendServices.access, 'emails'], // GET
    isPublicDomain: (): Endpoint => [BackendServices.organizations, 'is-public-domain'], // POST
    getOwnersList: (): Endpoint => [BackendServices.access, 'files/shared/owners/info'], // GET
    clearUserCache: (userId: string): Endpoint => [BackendServices.access, `users/${userId}`], // DELETE
    changeOwner: (workspaceId: string): Endpoint => [BackendServices.access, `files/${workspaceId}/owner`], // PATCH
    // links
    createMultipleFilesLink: (): Endpoint => [BackendServices.access, 'files/multiple_link'], // POST
    // batch download
    startBatchDownload: (): Endpoint => [BackendServices.access, 'files/batch-download'], // POST
    getBatchDownload: (batchId: string): Endpoint => [BackendServices.access, `files/batch-download/${batchId}`], // GET
    // policy
    filePolicy: (fileId: string): Endpoint => [BackendServices.access, `files/${fileId}/policy`], // PUT
    getPolicyConfirmed: (fileId): Endpoint => [BackendServices.access, `files/${fileId}/trypolicy`], // PUT
    workspaceNotify: (workspaceId): Endpoint => [BackendServices.access, `files/${workspaceId}/notify`], // POST
};

const ADMIN_ENDPOINTS = {
    getApiSettings: (): Endpoint => [BackendServices.admin, 'apikeys'], // GET
    getApiSettingsStorageKeys: (storage): Endpoint => (
        [BackendServices.admin, `apikeys/fields?service=${storage}`]
    ), // GET
    policies: (): Endpoint => [BackendServices.admin, 'policies?fields=settings'], // GET
    policy: (id?: string): Endpoint => [BackendServices.admin, `policies${id ? `/${id}` : ''}`], // GET
    checkIsOrgDomain: (domain: string): string => `${BackendServices.admin}check-org-domain?domain=${domain}`, // GET
    checkIPAccess: (): string => `${BackendServices.admin}check_ip`, // GET
    getConnectorHealth: (connectorType: string): Endpoint => (
        [BackendServices.admin, `connectors/${connectorType}/health`]
    ), // GET
    getConnectorManifest: (connectorType: string): Endpoint => (
        [BackendServices.admin, `connectors/${connectorType}/manifest`]
    ), // GET
    getSharepointKeysStatus: (organizationId?: string): string => (
        organizationId
            ? `${BackendServices.admin}jwt/organizations/${organizationId}/sharepoint/keys?account_type=client`
            : `${BackendServices.admin}jwt/sharepoint/keys?account_type=client`
    ), // GET — JWT authorizer; use string path to skip /ext/ proxy prefix
    generateSharepointKeys: (organizationId?: string): string => (
        organizationId
            ? `${BackendServices.admin}jwt/organizations/${organizationId}/sharepoint/keys/generate`
            : `${BackendServices.admin}jwt/sharepoint/keys/generate`
    ), // POST — JWT authorizer; use string path to skip /ext/ proxy prefix
};


const PASSWORD_ENDPOINTS = {
    getUserPassword: (userIdentifier: string, organizationId?: string): string => {
        const query = organizationId ? `?organization_id=${organizationId}` : '';
        return `${BackendServices.passwordService}users/${encodeURIComponent(userIdentifier)}/password${query}`;
    }, // GET
    getUserPasswordPublic: (userIdentifier: string, organizationId?: string): string => {
        const query = organizationId ? `?organization_id=${organizationId}` : '';
        return `${BackendServices.passwordService}public/users/${encodeURIComponent(userIdentifier)}/password${query}`;
    }, // GET
    setUserPassword: (userIdOrEmail: string): Endpoint => [BackendServices.passwordService, `users/${userIdOrEmail}/password`], // POST
    deleteUserPassword: (userIdOrEmail: string): Endpoint => [BackendServices.passwordService, `users/${userIdOrEmail}/password`], // DELETE
};

const ORGANIZATIONS_ENDPOINTS = {
    getUserMaxRole: (): Endpoint => [BackendServices.organizations, `user/max-role`], // GET
    getOrganizationsUsers: (
        page_size: number,
        page_token?: string,
        roles: { Administrators?: boolean; Collaborators?: boolean; Auditors?: boolean; Externals?: boolean } = {
            Administrators: true,
            Collaborators: true,
        },
    ): Endpoint => {
        const params = new URLSearchParams({
            page_size: page_size.toString(),
        });

        if (page_token && Object.keys(page_token).length > 0) {
            params.append("page_tokens", JSON.stringify(page_token));
        }
        if (roles) {
            Object.entries(roles).forEach(([role, enabled]) => {
                if (enabled) params.append(role, 'true');
            });
        }

        return [BackendServices.organizations,
        `organizations/users?${params.toString()}`]
    },
    getOrganizationsEmails: (): Endpoint => [BackendServices.organizations, 'organizations/emails'], // GET
    assignUserToTheOrganization:
        (id?: string): Endpoint => [BackendServices.organizations, `organizations/users${id ? `/${id}` : ''}`], // POST
    inviteUserToTheOrganization: (): Endpoint => [BackendServices.organizations, 'organizations/invite-users'], // POST
    changeOrganizationUserRole:
        (id: string): Endpoint => [BackendServices.organizations, `organizations/users/${id}`], // PUT
    deleteOrganizationUser:
        (id?: string): Endpoint => [BackendServices.organizations, `organizations/users${id ? `/${id}` : ''}`], // DELETE
};

const AUDITS_ENDPOINTS = {
    getAudits: (size?: number, queryObject?: Dict): Endpoint => {
        const queryString = dictToQuery(queryObject);
        const URLSecondPart = queryString ? `actions?pageSize=${size}&${queryString}` : `actions?pageSize=${size}`;
        return [BackendServices.audit, URLSecondPart];
    }, // GET
    generateEmailBecomeLicensedUser: (): Endpoint => [BackendServices.audit, 'notifications'], // POST
};

const AUTH_ENDPOINTS = {
    checkHasMfa: (emailList): string => `${BackendServices.auth}missing_phones?email=${emailList}`, // GET
    sendAuthStep: (): string => `${BackendServices.auth}step/send`, // POST
    /** @deprecated Use sendAuthStep instead */
    getPhoneCode: (): string => `${BackendServices.auth}step/send`, // POST - deprecated alias
    getMyPhoneNumber: (): string => `${BackendServices.auth}phone`, // GET
    setPhoneNumber: (email: string): Endpoint => [BackendServices.auth, `${email}/phone`], // POST
    getPhoneByEmail: (email: string): Endpoint => [BackendServices.auth, `${email}/phone`], // GET
    isGoogleRegister: (): string => `${BackendServices.auth}google`,
    isBoxRegister: (): string => `${BackendServices.auth}box/me`, // GET
    linkBoxAccount: (): string => `${BackendServices.auth}box`, // POST
    generateCode: (email: string, authType: OTPAuthType, linkId: string): string => (
        `${BackendServices.auth}code/${email}/${authType}${linkId ? `?linkId=${linkId}` : ''}`
    ), // POST
    signUp: (): string => `${BackendServices.auth}signup`, // POST
    verifyFileAccess: (): string => `${BackendServices.auth}step/verify`, // POST
    deleteSessionCache: (): Endpoint => [BackendServices.auth, `session-cache`], // DELETE
};

const ENTITIES_ENDPOINTS = {
    users: (): Endpoint => [BackendServices.entities, 'users'], // GET, PATCH
    roles: (): Endpoint => [BackendServices.entities, 'roles'], // GET, PATCH
    group: (groupId: string): Endpoint => [BackendServices.entities, `groups/${groupId}`], // GET, DELETE
    groups: (): Endpoint => [BackendServices.entities, 'groups'], // GET, PUT
    groupUsers: (groupId: string): Endpoint => [BackendServices.entities, `groups/${groupId}/users`], // GET, PATCH
    deleteUser: (userId: string): Endpoint => [BackendServices.entities, `users/${userId}`], // DELETE
    getMyUserInfo: (requiredFields: string): Endpoint => [BackendServices.entities, `users/me?required_fields=${requiredFields}`], // GET
    getUserPassword: (userIdOrEmail: string): Endpoint => (
        [BackendServices.entities, `users/${userIdOrEmail}/password`]
    ), // GET
};

const LINKS_ENDPOINTS = {
    getLinkDetails: (linkId: string): string => `${BackendServices.links}links/${linkId}`, // GET — public, sanitized 
    getLinkAuthFlow: (linkId: string): string => `${BackendServices.links}links/${linkId}/auth-flow`, // GET (public)
    // SaaS Cognito IAM — full payload after recipient auth.
    getSessionLinkDetails: (linkId: string): Endpoint => [BackendServices.links, `links/${linkId}/session`], // GET
    // On-prem / API_PROXY — API-key /ext path. String so resolveEndpoint does not
    // double-prefix ext/ (see EndpointsResolver.resolveEndpoint).
    getExtLinkDetails: (linkId: string): string => `${BackendServices.links}ext/links/${linkId}`, // GET
};

const UPLOAD_ENDPOINTS = {
    getUploadDetailsMultipart: (): Endpoint => [BackendServices.upload, 'multipart'], // POST,
    getFileUploadURL: (uploadId, part): Endpoint => (
        [BackendServices.upload, `multipart/${uploadId}/${part}/url`]
    ), // POST
    completeMultipartFiles: (uploadId): Endpoint => [BackendServices.upload, `multipart/${uploadId}/complete`], // POST
    uploadFile: (): Endpoint => [BackendServices.upload, 'files'], // POST
    overwriteFile: (fileId: string): Endpoint => [BackendServices.upload, `files/${fileId}`], // PUT
    uploadAppInfo: (): string => `${BackendServices.upload}appinfo`, // GET
};

const RULES_ENDPOINTS = {
    applyRules: (): Endpoint => [BackendServices.rules, 'apply'], // POST
};

const CONFIGURATIONS_ENDPOINTS = {
    // GET — stage-level UI configuration (e.g. whether the Administrators menu is shown).
    // Tuple form so on-prem / API_PROXY users are resolved to the `/ext/` API-key route.
    getUIConfiguration: (): Endpoint => [BackendServices.configurations, 'ui-configuration'],
};

const PURVIEW_ENDPOINTS = {
    getPurviewLabels: (): Endpoint => [BackendServices.purview, 'labels'], // GET
    syncPurviewLabels: (): Endpoint => [BackendServices.purview, 'admin/labels/sync'], // POST
};

// TODO: create separate endpoint collections for each BE service
export const ENDPOINTS = {
    ...ACCESS_ENDPOINTS,
    ...ADMIN_ENDPOINTS,
    ...AUDITS_ENDPOINTS,
    ...AUTH_ENDPOINTS,
    ...ENTITIES_ENDPOINTS,
    ...LINKS_ENDPOINTS,
    ...UPLOAD_ENDPOINTS,
    ...ORGANIZATIONS_ENDPOINTS,
    ...PASSWORD_ENDPOINTS,
    ...RULES_ENDPOINTS,
    ...PURVIEW_ENDPOINTS,
    ...CONFIGURATIONS_ENDPOINTS,
    appInfo: (): string => BackendServices.appinfo, // GET
};
